import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IUser } from '@interfaces/user';
import { IUsergroup } from '@interfaces/usergroup';
import { TranslocoService } from '@jsverse/transloco';
import posthog from 'posthog-js';
import { ReplaySubject } from 'rxjs';
import { AppSettings as appSettings } from '../app.settings';
import { HttpService } from './http.service';
// required for transloco-keys-manager
import {} from '@jsverse/transloco';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  user: IUser;
  testedUser?: IUser;

  /** fires when user object is updated */
  userUpdated$ = new ReplaySubject();

  constructor(
    public http: HttpService,
    public router: Router,
    public translocoService: TranslocoService,
  ) {}

  /**
   * Concat groups object to current user permissions
   */

  concatUserGroupPermissions(groups?: Partial<IUsergroup>[]) {
    if (groups) {
      for (const group of groups) {
        this.user.clientPermissions = [...(this.user.clientPermissions || []), ...(group.clientPermissions || [])];
        this.user.secretPermissions = [...(this.user.secretPermissions || []), ...(group.secretPermissions || [])];
        this.user.sitePermissions = [...(this.user.sitePermissions || []), ...(group.sitePermissions || [])];
        this.user.kohodocumentPermissions = [
          ...(this.user.kohodocumentPermissions || []),
          ...(group.kohodocumentPermissions || []),
        ];
        this.user.queuePermissions = [...(this.user.queuePermissions || []), ...(group.queuePermissions || [])];
      }
    }
  }

  /** Updates active UI language to user.language and falls back to navigator language if necessary */
  setUILanguageToUserLanguage() {
    if (this.user.language) {
      this.translocoService.setActiveLang(this.user.language);
    } else {
      const browserLang = navigator.language.substring(0, 2);
      const lang = browserLang === 'sv' ? 'sv' : browserLang === 'en' ? 'en' : 'fi';

      this.translocoService.setActiveLang(lang);
    }
  }

  async loadUser() {
    try {
      const { user, xsrf } = await this.http.get('/me');

      this.user = user;
      this.http.xsrf = xsrf;

      this.setUILanguageToUserLanguage();

      this.concatUserGroupPermissions(this.user.groups);
      this.userUpdated$.next(1);

      posthog.identify(this.user._id, {
        email: this.user.upn,
        name: this.user.name,
        office: this.user.office,
        company: this.user.company,
        department: this.user.department,
        title: this.user.title,
      });
    } catch (e) {
      console.warn(`userService.loadUser: ${e}`);

      throw e;
    }
  }

  setTestedUser(testedUser: IUser) {
    this.testedUser = testedUser;

    this.user._id = testedUser._id;
    this.user.name = testedUser.name;
    this.user.title = testedUser.title;
    this.user.department = testedUser.department;
    this.user.office = testedUser.office;
    this.user.company = testedUser.company;
    this.user.phone = testedUser.phone;
    this.user.upn = testedUser.upn;
    this.user.manager = testedUser.manager;
    this.user.language = testedUser.language;
    this.user.admin = testedUser.admin;
    this.user.groups = testedUser.groups;
    this.user.clientPermissions = testedUser.clientPermissions;
    this.user.sitePermissions = testedUser.sitePermissions;
    this.user.secretPermissions = testedUser.secretPermissions;
    this.user.kohodocumentPermissions = testedUser.kohodocumentPermissions;
    this.user.queuePermissions = testedUser.queuePermissions;
    this.user.favorites = testedUser.favorites;
    this.user.pulseSettings = testedUser.pulseSettings;

    this.concatUserGroupPermissions(testedUser.groups);
    this.userUpdated$.next(1);
  }

  resetTestedUser() {
    window.location.reload();
  }

  async waitForUser() {
    return new Promise((resolve, reject) => {
      if (this.user) {
        return resolve(true);
      }

      const interval = setInterval(() => {
        if (this.user) {
          clearInterval(interval);

          return resolve(true);
        }
      }, 250);
    });
  }

  userHasSitePermission(sitePermission: string | string[], redirect = false, strictPermission = false) {
    // console.log('userHassitePermission: ' + sitePermission);

    // Jos käyttäjäobjektia ei ole ollenkaan -> ei oikeuksia
    if (!this.user) {
      return this.forbidden(redirect);
    }

    // Jos käyttäjä on admin -> kaikki sallittua
    if (this.user.admin && !strictPermission) {
      return true;
    }

    // Jos default sitePermission, eli kaikille sallittu
    if (sitePermission === 'default') {
      return true;
    }

    if (typeof sitePermission === 'string' && appSettings.allowedPages.includes(sitePermission)) {
      return true;
    }

    // Jos käyttäjällä ylipäätään on oikeuksia
    if (this.user.sitePermissions) {
      if (typeof sitePermission === 'string') {
        // Jos oikeus löytyy käyttäjän oikeuksista niin -> OK
        if (this.user.sitePermissions.includes(sitePermission)) {
          return true;
        }

        // Jos vaadittu sitePermission päättyy tähteen, niin se tarkoitta, että kaikki alaoikeudet riittää
        // Esim clients* tarkoittaa, että clients_read ja clients_edit on OK
        if (sitePermission[sitePermission.length - 1] === '*') {
          const cleanSitePermission = sitePermission.substring(0, sitePermission.length - 1);

          for (const userSitePermission of this.user.sitePermissions) {
            if (userSitePermission.startsWith(cleanSitePermission)) {
              return true;
            }
          }
        }
      } else {
        // Jos sitePermission on array of strings
        for (const permission of sitePermission) {
          if (this.user.sitePermissions.includes(permission)) {
            return true;
          }
        }
      }
    }

    return this.forbidden(redirect);
  }

  userHasClientPermission(clientPermission: string | string[], redirect = false, strictPermission = false) {
    // Jos käyttäjäobjektia ei ole ollenkaan -> ei oikeuksia
    if (!this.user) {
      return this.forbidden(redirect);
    }

    // Jos käyttäjä on admin -> kaikki sallittua
    if (this.user.admin && !strictPermission) {
      return true;
    }

    // Jos käyttäjällä ylipäätään on oikeuksia
    if (this.user.clientPermissions) {
      if (typeof clientPermission === 'string') {
        // Jos oikeus löytyy käyttäjän oikeuksista niin -> OK
        if (this.user.clientPermissions.includes(clientPermission)) {
          return true;
        }
      } else {
        // Jos clientPermission on array of strings
        for (const permission of clientPermission) {
          if (this.user.clientPermissions.includes(permission)) {
            return true;
          }
        }
      }
    }

    return this.forbidden(redirect);
  }

  /** Check whether user belongs to one of groups (groupIds) that are given as the first argument */
  userBelongsToOneOfGroups(groupIds?: string[]): boolean {
    if (!groupIds) {
      return false;
    }

    const userGroupIds = this.user.groups.map((group) => group._id);

    for (const groupId of groupIds) {
      if (userGroupIds?.includes(groupId)) {
        return true;
      }
    }

    return false;
  }

  forbidden(redirect: boolean) {
    if (redirect) {
      this.router.navigate(['forbidden']);
    }

    return false;
  }

  async quickSearch(searchTerm: string) {
    const items = await this.http.get('/users/quicksearch', {
      params: {
        searchTerm,
      },
    });

    return items;
  }

  async getUserById(id: string) {
    const user = await this.http.get(`/users/${id}`);

    return user;
  }

  addFavoritePropertyToItems<T extends { _id: string }>(tag: string, data: T[]): (T & { favorite: boolean })[] {
    const favorites = this.user.favorites?.[tag] || [];

    return data.map((item) => ({
      ...item,
      favorite: favorites.includes(String(item._id)),
    }));
  }

  async toggleFavorite(tag: string, id: string, isFavorite: boolean) {
    await this.http.post(`/user/toggle-favorite`, { tag, id, isFavorite });
    this.user.favorites = this.user.favorites || {};
    this.user.favorites[tag] = this.user.favorites[tag] || [];
    this.user.favorites[tag] = isFavorite
      ? [...new Set([...this.user.favorites[tag], id])]
      : this.user.favorites[tag].filter((i: string) => i !== id);
  }

  getEmailSignatureHtml(): string {
    const greeting = this.translocoService.translate('emailGreeting');

    const parts = [
      `<strong>${greeting}</strong>`,
      '',
      this.user?.name || '',
      this.user?.title || '',
      this.user?.phone || '',
      this.user?.upn || '',
    ].filter((part) => part !== undefined);

    const content = parts.join('<br>');

    return `<p style="color: #00437a; font-family: Verdana, sans-serif; font-size: 8pt;">${content}</p>`;
  }
}
